import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  BusinessCollectionCompType,
  IBusinessCollectionProps,
} from '../../Types/ObjektVy';
import Logger from '../../Utils/Logger';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import backgroundImage from '../../Assets/Backgrounds/errorBackground.svg';
import EDButton from '../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../BaseComponents/Icon/EDIcon';
import { Link } from 'react-router';
import { EDControlledExpansionPanel } from '../../BaseComponents/Accordion/EDExpansionPanel';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import EDBox from '../../BaseComponents/EDBox/EDBox';
import { BusinessDataFormContext } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { setGlobalRollbarId, useRollbarId } from './RollbarIdContext';
import { useCookieSettings } from '../CookieSettings/CookieSettingsContext';

interface IInternalErrorProps {
  /** Felmeddelande */
  message: string;
  /** Unikt id för komponenten, t.ex. ett guid */
  id: string;
  /** Namn på felet, används för att kategorisera felet i Rollbar (motsvarar name på Error) */
  errorName?: string;
  /** Ev. Error objekt */
  error?: Error;
  /** Children kan användas om man vill visa felet på något särskilt sätt (visas under message) */
  children?: React.ReactNode;
  /** Logga inte felet till rollbar (ska bara användas för att undvika dubbelrapportering av fel) */
  skipErrorLogging?: boolean;
}
interface ITimestampProps {
  bottom?: string;
  right?: string;
  top?: string;
  left?: string;
  borderTopRightRadius?: string;
  borderTopLeftRadius?: string;
  borderBottomRightRadius?: string;
  borderBottomLeftRadius?: string;
  positionFixed?: boolean;
  setBackground?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  divBase: {
    zIndex: 1001,
    backgroundColor: theme.palette.background.default,
    boxSizing: 'border-box',
    paddingTop: theme.spacing(18),
    paddingLeft: theme.spacing(62.5),
    paddingRight: theme.spacing(62.5),
    width: '100%',
    textAlign: 'center',
    backgroundImage: 'url("' + backgroundImage + '")',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '20% 80%',
    overflowY: 'auto',
    position: 'absolute',
    top: 0,
    right: theme.spacing(0),
    bottom: theme.spacing(0),
    margin: theme.spacing(0),
    [theme.breakpoints.down('xl')]: {
      paddingLeft: theme.spacing(40.5),
      paddingRight: theme.spacing(40.5),
    },
    [theme.breakpoints.down('lg')]: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(15),
      paddingRight: theme.spacing(15),
    },
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  buttonStyle: {
    backgroundColor: theme.palette.primary.main,
  },
  panelContent: {
    marginTop: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
  },
  contentTextWrapper: {
    padding: theme.spacing(0),
  },
  infoTitle: {
    margin: theme.spacing(0),
  },
  infoText: {
    marginTop: theme.spacing(0.5),
  },

  errorTextExpanded: {
    fontStyle: 'italic',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  errorText: {
    textOverflow: 'ellipsis' /* enables ellipsis */,
    whiteSpace: 'nowrap' /* keeps the text in a single line */,
    overflow: 'hidden',
    fontStyle: 'italic',
    margin: 'auto',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  linkStyle: {
    textDecoration: 'none',
  },
  buttonWrapper: {
    paddingBottom: theme.spacing(4),
  },
  errorTitle: {
    textAlign: 'center',
    fontWeight: '16px',
  },
}));

export const EDErrorTimeStamp: React.FC<
  React.PropsWithChildren<ITimestampProps>
> = ({
  //Om man vill ändra på vart timestampen ska sitta använd dessa
  bottom,
  right,
  top,
  left,
  //Om man vill ändra på border-radius använd dessa
  borderTopRightRadius,
  borderTopLeftRadius,
  borderBottomRightRadius,
  borderBottomLeftRadius,
  //En fixerad position
  positionFixed,
  //Sätt bakgrund
  setBackground,
}) => {
  const formContext = useContext(BusinessDataFormContext);
  const currentError = formContext?.error;
  const formattedTimestamp = React.useMemo(
    () => `${new Date().toLocaleString('sv-SE')}${currentError ? '' : ''}`,
    [currentError]
  );

  return (
    <EDBox>
      <EDTypography
        sx={(theme: any) => ({
          background: setBackground
            ? theme.customProperties.errorComponent.timestampError
            : 'none',
          position: positionFixed ? 'fixed' : 'static',
          bottom: bottom,
          right: right,
          left: left,
          top: top,
          borderTopRightRadius: borderTopRightRadius,
          borderTopLeftRadius: borderTopLeftRadius,
          borderBottomRightRadius: borderBottomRightRadius,
          borderBottomLeftRadius: borderBottomLeftRadius,
          padding: theme.spacing(1),
          fontStyle: 'italic',
          zIndex: 10,
        })}
      >
        Felet registrerades: {formattedTimestamp}
      </EDTypography>
    </EDBox>
  );
};

interface IErrorTextProps {
  displayRollbarId: string | null;
}

export const ErrorText: React.FC<IErrorTextProps> = props => {
  const [consent] = useCookieSettings();

  return (
    <>
      <EDTypography variant="h4">
        Hoppsan, du har stött på ett problem i vårt system.
      </EDTypography>
      {props.displayRollbarId && consent.errorReports && (
        <>
          <EDTypography marginY={1}>
            Felet har loggats automatiskt med id {props.displayRollbarId}. Vårt
            team ser regelbundet över loggarna för att förbättra tjänsten
          </EDTypography>
          <EDTypography marginBottom={1}>
            Under tiden kan du kontrollera att du har navigerat till rätt rutin,
            gå tillbaka till startsidan alternativt ladda om din webbläsare. Om
            felet kvarstår är du välkommen att kontakta vår support!
          </EDTypography>
        </>
      )}
      {!props.displayRollbarId && consent.errorReports && (
        <>
          <EDTypography marginY={1}>
            Vänligen kontrollera att du har navigerat till rätt rutin, gå
            tillbaka till startsidan alternativt ladda om din webbläsare. Om
            felet kvarstår är du välkommen att kontakta vår support!
          </EDTypography>
        </>
      )}
      {!consent.errorReports && (
        <>
          <EDTypography marginY={1}>
            Baserat på dina integritetsinställningar har ingen information om
            problemet loggats hos oss. Kontrollera att du har navigerat till
            rätt rutin, gå tillbaka till startsidan alternativt ladda om din
            webbläsare.
          </EDTypography>
          <EDTypography marginBottom={1}>
            Om problemet kvarstår, rekommenderar vi att du kontaktar vår support
            och inkluderar den tekniska informationen nedan. Detta hjälper oss
            att förstå och åtgärda felet.
          </EDTypography>
        </>
      )}
    </>
  );
};

/**
 * Komponent som visar interna fel.
 * Den visar ett felmeddelande och en standardtext om att felet har loggats och att vi jobbar på att lösa det.
 */
export const InternalError = (props: IInternalErrorProps) => {
  const { classes } = useStyles();
  const [localRollbarId, setLocalRollbarId] = useState<string | null>(null);
  const { rollbarId: globalRollbarId } = useRollbarId();

  const displayRollbarId = props.skipErrorLogging
    ? globalRollbarId
    : localRollbarId;

  const refreshPage = () => {
    window.location.reload();
  };
  useEffect(() => {
    if (!props.skipErrorLogging) {
      Logger.log(
        'error',
        'InternalError-' + props.errorName,
        props.message,
        props.error,
        undefined,
        {
          reporter: 'InternalErrorComponent -' + props.id,
        }
      ).then(rollbarErrorId => {
        setLocalRollbarId(rollbarErrorId);
      });
    }
    return () => {
      setGlobalRollbarId(null); // Reset global Rollbar ID when component unmounts
    };
  }, [
    props.error,
    props.errorName,
    props.id,
    props.message,
    props.skipErrorLogging,
  ]);
  const [expanded, setExpanded] = useState(false);
  const onChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, newExpanded: boolean) => {
      setExpanded(newExpanded);
    },
    []
  );

  return (
    <div className={classes.divBase}>
      <div className={classes.contentTextWrapper}>
        <ErrorText displayRollbarId={displayRollbarId} />
        <div className={classes.panelContent}>
          <div className={classes.buttonWrapper}>
            <Link to={'/'} className={classes.linkStyle}>
              <EDButton
                icon={<EDIcon iconType="dashboard" color="inherit" />}
                color="primary"
                flat
                size="large"
                marginTop={1}
                marginRight={1}
                label="Startsidan"
              />
            </Link>

            <EDButton
              icon={<EDIcon iconType="refresh" color="inherit" />}
              flat
              size="large"
              onClick={refreshPage}
              color="primary"
              marginTop={1}
              marginRight={1}
              label="Ladda om"
            />
          </div>
          <EDErrorTimeStamp
            bottom="0px"
            right="0px"
            borderTopLeftRadius="5px"
            positionFixed={true}
            setBackground={true}
          />

          <EDTypography
            className={classes.errorTitle}
            color="primary"
            variant="body2"
          >
            Teknisk information
          </EDTypography>
          <EDBox>
            <EDControlledExpansionPanel
              overflowHidden
              expanded={expanded}
              onChange={onChange}
              lightBackgroundColor
              label={
                <EDTypography className={classes.errorText}>
                  {expanded ? '' : props.message}
                </EDTypography>
              }
            >
              <EDTypography className={classes.errorTextExpanded}>
                {props.message}
              </EDTypography>
              <EDTypography>{props.children}</EDTypography>
            </EDControlledExpansionPanel>
          </EDBox>
        </div>
      </div>
    </div>
  );
};

/** Skapar en InternalError komponent med fast id och meddelande */
export const internalErrorComp = (id: string, message: string) => {
  const InternalErrorWithStaticProps: React.FC<
    React.PropsWithChildren<Omit<IInternalErrorProps, 'id' | 'message'>>
  > = runtimeProps => (
    <InternalError {...runtimeProps} id={id} message={message} />
  );

  return InternalErrorWithStaticProps;
};

//** Skapar en BusinessCollectionComp som visar ett felmeddelande */
export const internalErrorBusinessComp =
  (errorId: string, message: string): BusinessCollectionCompType =>
  (props: IBusinessCollectionProps) =>
    (
      <InternalError
        id={errorId}
        message={`Fel för objekt med id: ${props.id}. ${message}`}
      />
    );
